import { useEffect, useMemo, useRef } from 'react';
import { getAvatarVars, toFullSteamAvatar } from '@/utils/avatar';
import { getUserColor } from '@/utils/colors';
import { IUser } from '@/utils/types/User';
import { useSyncedFlip } from '@/utils/hooks/use-synced-flip';

interface Props {
    p1?: IUser;
    p2?: IUser;
    winnerSide: 1 | 2;
    flipStartAt?: number | null;
    revealAt?: number | null;
}

const TURNS = 8;
const COIN_SIZE = 60;

const CoinflipInlineCoin = ({ p1, p2, winnerSide, flipStartAt, revealAt }: Props) => {
    const endDeg = TURNS * 360 + (winnerSide === 1 ? 0 : 180);
    const coinRef = useRef<HTMLDivElement>(null);

    // Same server-synced RAF spin as the big modal coin, so the list row and the
    // open modal land on the same face at the same instant.
    useSyncedFlip({ ref: coinRef, endDeg, active: true, flipStartAt, revealAt });

    // Preload Steam avatars so they're in cache before the spin starts
    useEffect(() => {
        if (typeof window === 'undefined') return;
        [p1?.avatar, p2?.avatar].forEach((url) => {
            if (!url) return;
            const img = new window.Image();
            img.src = url;
        });
    }, [p1?.avatar, p2?.avatar]);

    return (
        <div
            className="w-[130px] h-[60px] flex items-center justify-center"
            style={{ perspective: '700px' }}
        >
            <div
                ref={coinRef}
                className="relative"
                style={
                    {
                        width: COIN_SIZE,
                        height: COIN_SIZE,
                        transformStyle: 'preserve-3d',
                        ['--coin-end' as string]: `${endDeg}deg`,
                    } as React.CSSProperties
                }
            >
                <CoinFace user={p1} side={1} />
                <CoinFace user={p2} side={2} />
            </div>
        </div>
    );
};

function CoinFace({ user, side }: { user?: IUser; side: 1 | 2 }) {
    // Border matches the user's LEVEL colour (same as the static row avatars),
    // not the global brand --p1/--p2 accent.
    const color = user
        ? getUserColor(user.experience ?? 0)
        : side === 1
          ? 'var(--p1)'
          : 'var(--p2)';
    const seed = user?.name || String(user?.id ?? '');
    const vars = useMemo(() => getAvatarVars(seed), [seed]);

    return (
        <div
            style={{
                position: 'absolute',
                inset: 0,
                borderRadius: '28%',
                overflow: 'hidden',
                backfaceVisibility: 'hidden',
                WebkitBackfaceVisibility: 'hidden',
                boxShadow: `0 0 0 2.5px ${color}, 0 0 18px -2px ${color}, inset 0 0 18px rgba(0,0,0,0.45)`,
                transform: side === 2 ? 'rotateY(180deg)' : undefined,
                background: '#14161c',
            }}
        >
            {user?.avatar ? (
                <img
                    src={toFullSteamAvatar(user.avatar)}
                    alt=""
                    width={COIN_SIZE}
                    height={COIN_SIZE}
                    draggable={false}
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                />
            ) : user ? (
                <div
                    className="abstract-avatar aa-square"
                    style={{ ...(vars as React.CSSProperties), width: '100%', height: '100%' }}
                    aria-hidden
                />
            ) : (
                <div className="w-full h-full" />
            )}
            <div
                aria-hidden
                style={{
                    position: 'absolute',
                    inset: 0,
                    borderRadius: '28%',
                    pointerEvents: 'none',
                    background:
                        'radial-gradient(circle at 30% 22%, rgba(255,255,255,0.18), transparent 45%), radial-gradient(circle at 70% 80%, rgba(0,0,0,0.30), transparent 50%)',
                }}
            />
        </div>
    );
}

export default CoinflipInlineCoin;
